import { useEffect, useRef, useState } from 'react';
import { Bell, CheckCheck } from 'lucide-react';
import { cn, timeAgo } from '@/shared/utils/utils';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { useNotifications, useNotificationMutations } from '../hooks/useNotifications';
import { resolveNotif } from '../constants';
import type { NotificationItem } from '../types';

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { data, isLoading } = useNotifications(20);
  const { markRead, markAllRead } = useNotificationMutations();

  const notifications = data?.notifications ?? [];
  const unread = data?.unreadCount ?? 0;

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const onRowClick = (n: NotificationItem) => {
    if (!n.is_read) markRead.mutate(n.id);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative flex h-9 w-9 items-center justify-center rounded-full text-on-surface-variant hover:bg-surface-variant"
        aria-label={`Notifications${unread ? `, ${unread} unread` : ''}`}
      >
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[10px] font-semibold leading-none text-white">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-40 mt-2 w-80 overflow-hidden rounded-card border border-outline bg-surface shadow-surface-2 animate-fade-in">
          <div className="flex items-center justify-between border-b border-outline px-4 py-3">
            <span className="text-data-md font-semibold text-on-surface">Notifications</span>
            {unread > 0 && (
              <button
                type="button"
                onClick={() => markAllRead.mutate()}
                disabled={markAllRead.isPending}
                className="flex items-center gap-1 text-body-sm text-primary hover:underline disabled:opacity-50"
              >
                <CheckCheck className="h-3.5 w-3.5" /> Mark all read
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Spinner />
              </div>
            ) : notifications.length === 0 ? (
              <div className="px-4 py-6">
                <EmptyState title="No notifications" description="You're all caught up." />
              </div>
            ) : (
              notifications.map((n) => {
                const meta = resolveNotif(n.type);
                const Icon = meta.icon;
                return (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => onRowClick(n)}
                    className={cn(
                      'flex w-full items-start gap-3 border-b border-outline px-4 py-3 text-left transition-colors last:border-0 hover:bg-surface-variant',
                      !n.is_read && 'bg-primary-container/30',
                    )}
                  >
                    <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-surface-variant text-on-surface-variant">
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block break-words text-data-md text-on-surface">{n.message}</span>
                      <span className="mt-0.5 block text-label-caps uppercase text-on-surface-variant/70">
                        {timeAgo(n.created_at)}
                      </span>
                    </span>
                    {!n.is_read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" aria-label="Unread" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
